import React, { useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  Grid,
  Stack,
  TextField,
  Typography,
  Alert,
  Avatar,
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '../../store/authStore';
import {
  getSchoolDirect,
  updateSchoolDirect,
  uploadSchoolLogoDirect,
} from '@timetable/api';
import { LoadingSpinner } from '../LoadingSpinner';
import { AcademicYearManager } from './AcademicYearManager';

export const SchoolSettings: React.FC = () => {
  const user = useAuthStore((state) => state.user);
  const schoolContext = useAuthStore((state) => state.schoolContext);
  const queryClient = useQueryClient();

  // Determine effective school ID
  // Super Admin: use schoolContext (selected school)
  // Regular users: use their school_id
  const effectiveSchoolId = user?.role === 'super_admin' 
    ? schoolContext 
    : user?.school_id;

  const [schoolName, setSchoolName] = useState('');
  const [primaryColor, setPrimaryColor] = useState('#1976D2');
  const [secondaryColor, setSecondaryColor] = useState('#FF9800');
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);

  // Query school settings
  const { data: school, isLoading } = useQuery({
    queryKey: ['school', effectiveSchoolId],
    queryFn: () => getSchoolDirect(effectiveSchoolId!),
    enabled: !!effectiveSchoolId,
  });

  // Update state when school data is loaded
  React.useEffect(() => {
    if (school) {
      setSchoolName(school.school_name);
      setPrimaryColor(school.primary_color || '#1976D2');
      setSecondaryColor(school.secondary_color || '#FF9800');
      if (school.logo_url) {
        setLogoPreview(school.logo_url);
      }
    }
  }, [school]);

  // Update school mutation
  const updateMutation = useMutation({
    mutationFn: (updates: { school_name?: string; primary_color?: string; secondary_color?: string; logo_url?: string }) =>
      updateSchoolDirect(effectiveSchoolId!, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['school'] });
    },
  });

  // Upload logo mutation
  const uploadLogoMutation = useMutation({
    mutationFn: (file: File) => uploadSchoolLogoDirect(effectiveSchoolId!, file),
    onSuccess: (logoUrl) => {
      // Update school with new logo URL
      updateMutation.mutate({ logo_url: logoUrl });
      setLogoPreview(logoUrl);
      setLogoFile(null);
      setUploadError(null);
    },
    onError: (error: Error) => {
      setUploadError(error.message);
    },
  });

  const handleLogoChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['image/png', 'image/jpeg', 'image/svg+xml'];
    if (!validTypes.includes(file.type)) {
      setUploadError('Invalid file type. Only PNG, JPG, and SVG are supported.');
      return;
    }

    // Validate file size (2MB max)
    const maxSize = 2 * 1024 * 1024;
    if (file.size > maxSize) {
      setUploadError('File size exceeds 2MB limit.');
      return;
    }

    setLogoFile(file);
    setUploadError(null);

    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setLogoPreview(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  const handleUploadLogo = () => {
    if (logoFile) {
      uploadLogoMutation.mutate(logoFile);
    }
  };

  const handleSaveSettings = () => {
    updateMutation.mutate({
      school_name: schoolName,
      primary_color: primaryColor,
      secondary_color: secondaryColor,
    });
  };

  if (isLoading) {
    return <LoadingSpinner />;
  }

  // Show message if Super Admin hasn't selected a school
  if (user?.role === 'super_admin' && !schoolContext) {
    return (
      <Alert severity="info">
        Please select a school from the school context switcher to view and edit its settings.
      </Alert>
    );
  }

  // Show message if no school context available
  if (!effectiveSchoolId) {
    return (
      <Alert severity="warning">
        No school context available. Please contact your administrator.
      </Alert>
    );
  }

  return (
    <Stack spacing={4}>
      {/* Super Admin indicator */}
      {user?.role === 'super_admin' && school && (
        <Alert severity="info">
          You are editing settings for: <strong>{school.school_name}</strong> ({school.school_id})
        </Alert>
      )}

      {/* School Information */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            School Information
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            Configure your school's basic information and branding.
          </Typography>

          <Grid container spacing={3} mt={1}>
            <Grid item xs={12} md={6}>
              <TextField
                label="School Name"
                value={schoolName}
                onChange={(e) => setSchoolName(e.target.value)}
                fullWidth
                required
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  School Logo
                </Typography>
                <Stack direction="row" spacing={2} alignItems="center">
                  <Avatar
                    src={logoPreview || undefined}
                    sx={{ width: 80, height: 80 }}
                    variant="rounded"
                  >
                    {schoolName.charAt(0)}
                  </Avatar>
                  <Box>
                    <Button
                      component="label"
                      variant="outlined"
                      startIcon={<UploadIcon />}
                      size="small"
                    >
                      Choose File
                      <input
                        type="file"
                        hidden
                        accept="image/png,image/jpeg,image/svg+xml"
                        onChange={handleLogoChange}
                      />
                    </Button>
                    {logoFile && (
                      <Button
                        variant="contained"
                        size="small"
                        onClick={handleUploadLogo}
                        disabled={uploadLogoMutation.isPending}
                        sx={{ ml: 1 }}
                      >
                        Upload
                      </Button>
                    )}
                    <Typography variant="caption" display="block" color="text.secondary" mt={1}>
                      PNG, JPG, or SVG. Max 2MB.
                    </Typography>
                  </Box>
                </Stack>
                {uploadError && (
                  <Alert severity="error" sx={{ mt: 2 }}>
                    {uploadError}
                  </Alert>
                )}
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                label="Primary Color"
                type="color"
                value={primaryColor}
                onChange={(e) => setPrimaryColor(e.target.value)}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                label="Secondary Color"
                type="color"
                value={secondaryColor}
                onChange={(e) => setSecondaryColor(e.target.value)}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12}>
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={handleSaveSettings}
                disabled={updateMutation.isPending || !schoolName.trim()}
              >
                Save Settings
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Divider />

      {/* Academic Year Configuration */}
      <Box>
        <Typography variant="h6" gutterBottom>
          Academic Year Configuration
        </Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          Manage academic years and set the active year for the timetable.
        </Typography>
        <AcademicYearManager />
      </Box>
    </Stack>
  );
};
